import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { columns } from "./student-list-column-definition";
import commonStyles from "../../../style/common.module.css";
import { User } from "../../model/user";
import { getUserStudents } from "../../service/student-service";
import { ACCOUNT_MANAGEMENT_STUDENT_ADD, ACCOUNT_MANAGEMENT_STUDENT_EDIT, PARAMETER_STUDENT_ID } from "../../../../infrastructure/route";
import { STUDENT_TAB_STATE } from "../student-view";
import { useCache } from "../../../../context/cache/cache-provider";
import { BaseList } from "../../../../component/list/base-list";
import { Chip } from "@mui/material";
import { Role, studentRoles } from "../../../enum/role";

const studentTypeFilters = [
    { label: "All", role: null },
    ...studentRoles.map(role => ({ label: Role[role].replace(/([A-Z])/g, ' $1').trim(), role }))
];

export default function StudentList() {
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState<User[]>([]);
    const [selectedType, setSelectedType] = useState<Role | null>(null);

    const navigate = useNavigate();
    const location = useLocation();
    const cache = useCache();

    useEffect(() => {
        setLoading(true);
        getUserStudents().then(result => {
            setUsers(result);
            setLoading(false);
        }).catch(() => {});
    }, [location.key]);

    const filteredUsers = useMemo(() => {
        if (selectedType === null) return users;
        return users.filter(user => user.roles.includes(selectedType));
    }, [users, selectedType]);

    const openAdd = () => {
        localStorage.removeItem(STUDENT_TAB_STATE);
        navigate(ACCOUNT_MANAGEMENT_STUDENT_ADD);
    }

    const openEdit = (studentId: number) => {
        const student = users.find(_ => _.id === Number(studentId));
        cache.update({ key: PARAMETER_STUDENT_ID, value: student?.name ?? '' });

        localStorage.removeItem(STUDENT_TAB_STATE);
        navigate(ACCOUNT_MANAGEMENT_STUDENT_EDIT.replace(PARAMETER_STUDENT_ID, studentId.toString()))
    }

    return (
        <div className={commonStyles.content} >
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                {studentTypeFilters.map(filter => (
                    <Chip
                        key={filter.label}
                        label={filter.label}
                        color={selectedType === filter.role ? "primary" : "default"}
                        variant={selectedType === filter.role ? "filled" : "outlined"}
                        onClick={() => setSelectedType(filter.role)}
                    />
                ))}
            </div>
            <BaseList
                storageKey="STUDENT"
                rows={filteredUsers}
                columns={columns}
                loading={loading}
                localeText={{ noRowsLabel: "No students" }}
                getRowId={(row) => row?.id}
                onRowClick={(e) => openEdit(e.row?.id)}
                onAddClick={() => openAdd()}
                addButtonText="Add student"
                initialColumnVisibility={{ id: false }}
            />
        </div>
    );
}
